import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  DialogContent,
  DialogActions,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import CommonDialog from "app/components/common/CommonDialog";
import TextFieldLabel from "app/components/common/TextFieldLabel";
import {
  CustomTextField,
  CustomTextArea,
  CustomSelectBox,
} from "assets/styles/globalStyledComponent";
import { useMutation } from "react-query";
import { toast } from "react-toastify";
import {
  changePlan,
  upgradePlan,
  downgradePlan,
  cancelSubscription,
  renewSubscription,
} from "app/services/org/subscription.service";
import { DatePicker } from "@mui/x-date-pickers";

const ManageSubscriptionDialog = ({
  open = false,
  onClose = () => {},
  refetch = () => {},
  subscriptionData = {},
  planList = [],
  actionType = "change", // change, upgrade, downgrade, cancel, renew
}) => {
  const [action, setAction] = useState(actionType);
  const [notes, setNotes] = useState("");
  const [selectedPlan, setSelectedPlan] = useState("");
  const [renewalDate, setRenewalDate] = useState(null);

  const { control, handleSubmit, reset, watch } = useForm({
    defaultValues: {
      notes: "",
      newPlanId: "",
    },
  });

  useEffect(() => {
    if (!open) {
      reset();
      setNotes("");
      setSelectedPlan("");
      setRenewalDate(null);
    }
  }, [open, reset]);

  // Mutations
  const { mutate: changePlanMutate, isLoading: changePlanLoading } = useMutation(
    "changePlan",
    ({ subscriptionId, payload }) => changePlan(subscriptionId, payload),
    {
      onSuccess: (success) => {
        toast.success(success?.data?.message || "Plan changed successfully");
        refetch();
        onClose();
      },
      onError: (err) => {
        toast.error(err?.response?.data?.message || "Failed to change plan");
      },
    }
  );

  const { mutate: upgradePlanMutate, isLoading: upgradePlanLoading } = useMutation(
    "upgradePlan",
    ({ subscriptionId, payload }) => upgradePlan(subscriptionId, payload),
    {
      onSuccess: (success) => {
        toast.success(success?.data?.message || "Plan upgraded successfully");
        refetch();
        onClose();
      },
      onError: (err) => {
        toast.error(err?.response?.data?.message || "Failed to upgrade plan");
      },
    }
  );

  const { mutate: downgradePlanMutate, isLoading: downgradePlanLoading } = useMutation(
    "downgradePlan",
    ({ subscriptionId, payload }) => downgradePlan(subscriptionId, payload),
    {
      onSuccess: (success) => {
        toast.success(success?.data?.message || "Plan downgraded successfully");
        refetch();
        onClose();
      },
      onError: (err) => {
        toast.error(err?.response?.data?.message || "Failed to downgrade plan");
      },
    }
  );

  const { mutate: cancelSubscriptionMutate, isLoading: cancelSubscriptionLoading } =
    useMutation(
      "cancelSubscription",
      ({ subscriptionId, payload }) => cancelSubscription(subscriptionId, payload),
      {
        onSuccess: (success) => {
          toast.success(success?.data?.message || "Subscription cancelled successfully");
          refetch();
          onClose();
        },
        onError: (err) => {
          toast.error(err?.response?.data?.message || "Failed to cancel subscription");
        },
      }
    );

  const { mutate: renewSubscriptionMutate, isLoading: renewSubscriptionLoading } =
    useMutation(
      "renewSubscription",
      ({ subscriptionId, payload }) => renewSubscription(subscriptionId, payload),
      {
        onSuccess: (success) => {
          toast.success(success?.data?.message || "Subscription renewed successfully");
          refetch();
          onClose();
        },
        onError: (err) => {
          toast.error(err?.response?.data?.message || "Failed to renew subscription");
        },
      }
    );

  const onSubmit = () => {
    const subscriptionId = subscriptionData?.id;
    let payload = { notes };

    switch (action) {
      case "change":
        payload.newPlanId = selectedPlan;
        changePlanMutate({ subscriptionId, payload });
        break;
      case "upgrade":
        payload.newPlanId = selectedPlan;
        upgradePlanMutate({ subscriptionId, payload });
        break;
      case "downgrade":
        payload.newPlanId = selectedPlan;
        downgradePlanMutate({ subscriptionId, payload });
        break;
      case "cancel":
        cancelSubscriptionMutate({ subscriptionId, payload });
        break;
      case "renew":
        payload.renewalDate = renewalDate;
        renewSubscriptionMutate({ subscriptionId, payload });
        break;
      default:
        break;
    }
  };

  const isLoading =
    changePlanLoading ||
    upgradePlanLoading ||
    downgradePlanLoading ||
    cancelSubscriptionLoading ||
    renewSubscriptionLoading;

  const getDialogTitle = () => {
    switch (action) {
      case "change":
        return "Change Plan";
      case "upgrade":
        return "Upgrade Plan";
      case "downgrade":
        return "Downgrade Plan";
      case "cancel":
        return "Cancel Subscription";
      case "renew":
        return "Renew Subscription";
      default:
        return "Manage Subscription";
    }
  };

  return (
    <CommonDialog
      open={open}
      onClose={onClose}
      title={getDialogTitle()}
      maxWidth="sm"
    >
      <DialogContent sx={{ minWidth: "500px" }}>
        {/* Action Selection */}
        <FormControl component="fieldset" sx={{ mb: 2 }}>
          <FormLabel component="legend">Action Type</FormLabel>
          <RadioGroup
            row
            value={action}
            onChange={(e) => setAction(e.target.value)}
          >
            <FormControlLabel value="change" control={<Radio />} label="Change Plan" />
            <FormControlLabel value="upgrade" control={<Radio />} label="Upgrade" />
            <FormControlLabel value="downgrade" control={<Radio />} label="Downgrade" />
            <FormControlLabel value="cancel" control={<Radio />} label="Cancel" />
            <FormControlLabel value="renew" control={<Radio />} label="Renew" />
          </RadioGroup>
        </FormControl>

        <Grid container spacing={2}>
          {/* Plan Selection (for Change, Upgrade, Downgrade) */}
          {["change", "upgrade", "downgrade"].includes(action) && (
            <Grid item xs={12}>
              <TextFieldLabel>Select New Plan</TextFieldLabel>
              <CustomSelectBox
                size="small"
                fullWidth
                displayEmpty
                value={selectedPlan}
                onChange={(e) => setSelectedPlan(e.target.value)}
              >
                <MenuItem value="" disabled>
                  Select a plan
                </MenuItem>
                {planList?.map((plan) => (
                  <MenuItem key={plan.id} value={plan.id}>
                    {plan.planName} - ₹{parseFloat(plan.price).toFixed(2)} /{" "}
                    {plan.billingCycle}
                  </MenuItem>
                ))}
              </CustomSelectBox>
            </Grid>
          )}

          {/* Renewal Date (for Renew) */}
          {action === "renew" && (
            <Grid item xs={12}>
              <TextFieldLabel>Renewal Date</TextFieldLabel>
              <DatePicker
                value={renewalDate}
                onChange={(newValue) => setRenewalDate(newValue)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                  },
                }}
              />
            </Grid>
          )}

          {/* Notes */}
          <Grid item xs={12}>
            <TextFieldLabel>Notes (Optional)</TextFieldLabel>
            <CustomTextArea
              placeholder="Add any notes or reasons"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              style={{
                width: "100%",
                height: "80px",
                padding: "10px",
                fontSize: "14px",
                border: "1.5px solid #B6C8D6",
                borderRadius: "8px",
                fontFamily: "Inter, sans-serif",
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <LoadingButton
          onClick={onClose}
          variant="outlined"
          disabled={isLoading}
        >
          Cancel
        </LoadingButton>
        <LoadingButton
          onClick={onSubmit}
          loading={isLoading}
          variant="contained"
          color={action === "cancel" ? "error" : "primary"}
        >
          {getDialogTitle()}
        </LoadingButton>
      </DialogActions>
    </CommonDialog>
  );
};

export default ManageSubscriptionDialog;
